import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Phone } from "lucide-react";
import { useDataProvider, useNotify, useTranslate } from "ra-core";
import { Button } from "@/components/ui/button";

import type { CrmDataProvider } from "../providers/types";
import { useMyPhoneExt } from "./useMyPhoneExt";

/**
 * «Позвонить»: the PBX rings the user's phone first, then the client.
 * Hidden when the user has no extension (Settings shows why) or there is no
 * number to call. Without a PBX the server's «Звонок из CRM не настроен» is
 * shown as it is.
 */
export const CallButton = ({
  phone,
  className,
}: {
  phone?: string | null;
  className?: string;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { ext } = useMyPhoneExt();
  const { mutate, isPending } = useMutation({
    mutationFn: (number: string) => dataProvider.placeCall(number),
    onSuccess: () => {
      notify("crm.calls.calling", { type: "success" });
      // the PBX reports the call a moment later
      setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ["calls"] }),
        5000,
      );
    },
    onError: (error: Error) => {
      notify(error?.message || "crm.calls.call_error", {
        type: "error",
        messageArgs: { _: error?.message },
      });
    },
  });
  const number = phone?.trim();
  if (!ext || !number) return null;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={className}
      disabled={isPending}
      onClick={() => mutate(number)}
    >
      <Phone className="size-4" />
      {translate("crm.calls.call")}
    </Button>
  );
};
